import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Hover tooltip in the app's own styling, replacing the browser's native
 * `title=` popup.
 *
 * Two positioning strategies, same visuals:
 *
 * - Default: CSS-only (group-hover), absolute within the trigger. No state, no
 *   re-render on hover — but an ancestor with `overflow-hidden` (or a scroll
 *   container) CLIPS it.
 * - `fixed`: state-driven, rendered into document.body with `position: fixed`
 *   from the trigger's measured rect, so it escapes any overflow clipping.
 *   Used by the template tiles and the wiki linked-template rows, whose cards
 *   are `overflow-hidden` / scrollable.
 *
 * Unlike `CustomTooltip` in gantt/GanttPopups.js this never follows the
 * cursor; it anchors to the trigger.
 *
 * Defaults to appearing BELOW the trigger: most callers are toolbar buttons at
 * the top of their panel, where a tooltip above would clip.
 *
 * @param {string}  text      tooltip content; renders the child bare when empty
 * @param {string}  position  'bottom' (default) or 'top'
 * @param {boolean} fixed     portal + position:fixed (escapes overflow-hidden)
 * @param {string}  className extra classes on the wrapper span (e.g. 'min-w-0'
 *                            so a truncating child keeps truncating)
 * @param {object}  style     inline style on the wrapper span. For triggers the
 *                            wrapper has to POSITION rather than sit in flow -
 *                            an absolutely-placed drag handle or marker, whose
 *                            own `absolute` would otherwise resolve against the
 *                            wrapper instead of the bar. It must be a style and
 *                            not a class: the wrapper already carries
 *                            `relative`, and Tailwind emits `.relative` after
 *                            `.absolute`, so a class would silently lose.
 */
// Exported so NativeTooltips renders an identical bubble. One source of truth
// for the look: a restyle here must not leave the intercepted `title=` popups
// showing the old one.
export const BUBBLE_CLASSES = `pointer-events-none z-50 whitespace-nowrap rounded-lg bg-gray-800
    px-3 py-1.5 text-xs font-normal normal-case text-white shadow-lg`;

const Arrow = ({ below }) => (
    <span
        className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-transparent ${
            below
                ? 'bottom-full border-b-4 border-b-gray-800'
                : 'top-full border-t-4 border-t-gray-800'
        }`}
    />
);

const FixedTooltip = ({ text, children, position, className, style }) => {
    const [rect, setRect] = useState(null);
    const triggerRef = useRef(null);
    const below = position === 'bottom';

    return (
        <span
            ref={triggerRef}
            style={style}
            className={`relative inline-flex ${className}`}
            onMouseEnter={() => setRect(triggerRef.current.getBoundingClientRect())}
            onMouseLeave={() => setRect(null)}
        >
            {children}
            {rect && createPortal(
                <span
                    role="tooltip"
                    className={`fixed ${BUBBLE_CLASSES}`}
                    style={{
                        left: rect.left + rect.width / 2,
                        top: below ? rect.bottom + 8 : undefined,
                        bottom: below ? undefined : window.innerHeight - rect.top + 8,
                        transform: 'translateX(-50%)'
                    }}
                >
                    {text}
                    <Arrow below={below} />
                </span>,
                document.body
            )}
        </span>
    );
};

const Tooltip = ({ text, children, position = 'bottom', fixed = false, className = '', style }) => {
    if (!text) return children;

    if (fixed) {
        return (
            <FixedTooltip text={text} position={position} className={className} style={style}>
                {children}
            </FixedTooltip>
        );
    }

    const below = position === 'bottom';

    return (
        <span style={style} className={`relative inline-flex group ${className}`}>
            {children}
            <span
                role="tooltip"
                className={`absolute left-1/2 -translate-x-1/2 hidden group-hover:block ${BUBBLE_CLASSES}
                    ${below ? 'top-full mt-2' : 'bottom-full mb-2'}`}
            >
                {text}
                <Arrow below={below} />
            </span>
        </span>
    );
};

export default Tooltip;
